import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import {
  MonitoredOperationListItemResponse,
  OperationLogRecordResponse,
  OperationMonitoringService as OperationMonitoringApiService,
} from '@6grain/masofa-monolith-api-client/common';
import { BackButton } from '@components/ui/back-button/back-button';
import { Loading } from '@components/ui/loading/loading';
import { CatalogAuthorsComponent } from '@app/pages/admin/developer-catalog-page/catalog-authors/catalog-authors';
import { DeveloperCatalogStore } from '@app/pages/admin/developer-catalog-page/developer-catalog.store';
import {
  CatalogJob,
  catalogGroupLabel,
  developerCatalogCommandLink,
  formatExecutionSummary,
  formatExecutionTooltip,
  isCatalogItemObsolete,
  isOrchestrationCompliant,
  isLegacyCronJob,
  jobReferenceCount,
  orchestrationIssuesTooltip,
  runtimeSeverity,
} from '@app/pages/admin/developer-catalog-page/developer-catalog.utils';
import { serializeAdminHash } from '@app/pages/admin/admin-hash-state';
import {
  Subject,
  combineLatest,
  finalize,
  map,
  switchMap,
  takeUntil,
} from 'rxjs';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import { filterByCatalogQuery } from '@app/pages/admin/orchestration-catalog-search.util';

@Component({
  selector: 'app-developer-catalog-job-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    TranslateModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    BackButton,
    Loading,
    CatalogAuthorsComponent,
    CopyableIdentifierComponent,
    OrchestrationTableHeadingComponent,
    ServerTranslatePipe,
  ],
  templateUrl: './developer-catalog-job-page.html',
  styleUrl: './developer-catalog-job-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeveloperCatalogJobPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(DeveloperCatalogStore);
  private readonly monitoringService = inject(OperationMonitoringApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logRequestCancelled$ = new Subject<void>();
  private readonly executionsRequestCancelled$ = new Subject<void>();

  readonly commandLink = developerCatalogCommandLink;
  readonly runtimeSeverity = runtimeSeverity;
  readonly jobReferenceCount = jobReferenceCount;
  readonly isOrchestrationCompliant = isOrchestrationCompliant;
  readonly isLegacyCronJob = isLegacyCronJob;
  readonly isCatalogItemObsolete = isCatalogItemObsolete;
  readonly catalogGroupLabel = catalogGroupLabel;
  readonly orchestrationIssuesTooltip = orchestrationIssuesTooltip;
  readonly formatExecutionSummary = formatExecutionSummary;
  readonly formatExecutionTooltip = formatExecutionTooltip;
  readonly logLevels = ['Trace', 'Debug', 'Information', 'Warning', 'Error', 'Critical'];

  private readonly state = toSignal(
    this.route.paramMap.pipe(
      map((params) => params.get('name') ?? ''),
      switchMap((name) =>
        combineLatest([this.store.loading$, this.store.findJob(name)]).pipe(
          map(([loading, job]) => ({ loading, job, name })),
        ),
      ),
    ),
    { initialValue: { loading: true, job: null as CatalogJob | null, name: '' } },
  );

  loading = computed(() => this.state().loading);
  error = toSignal(this.store.error$, { initialValue: null as string | null });
  job = computed(() => this.state().job);
  notFound = computed(() => !this.state().loading && !this.state().job);
  title = computed(() => this.job()?.name || this.state().name || 'Job');

  readonly executions = signal<MonitoredOperationListItemResponse[]>([]);
  readonly executionSearch = signal('');
  readonly executionsLoading = signal(false);
  readonly executionsError = signal<string | null>(null);
  readonly selectedExecutionId = signal<string | null>(null);

  readonly visibleExecutions = computed(() =>
    filterByCatalogQuery(
      this.executions(),
      this.executionSearch(),
      execution => [execution.id, execution.state, execution.operationType, execution.name],
    ),
  );

  readonly operationLogs = signal<OperationLogRecordResponse[]>([]);
  readonly operationLogsLoading = signal(false);
  readonly operationLogsAvailable = signal(false);
  readonly operationLogsProvider = signal('');
  readonly operationLogsMessage = signal('');
  readonly operationLogsExternalUrl = signal<string | null>(null);
  readonly operationLogsHasMore = signal(false);
  readonly operationLogLevel = signal('');
  readonly operationLogSearch = signal('');

  constructor() {
    toObservable(this.job)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((job) => {
        this.executionsRequestCancelled$.next();
        this.resetLogs();
        this.selectedExecutionId.set(null);
        this.executions.set([]);
        this.executionsError.set(null);
        if (job?.name) {
          this.loadExecutions(job.name);
        }
      });

    this.destroyRef.onDestroy(() => {
      this.executionsRequestCancelled$.next();
      this.executionsRequestCancelled$.complete();
      this.logRequestCancelled$.next();
      this.logRequestCancelled$.complete();
    });
  }

  monitoringLink(jobName: string): string[] {
    return ['/admin/orchestration/monitoring'];
  }

  monitoringQueryParams(jobName: string): Record<string, string> {
    return { kind: 'JobExecution', search: jobName };
  }

  monitoringFragment(executionId: string): string | undefined {
    return serializeAdminHash({ kind: 'JobExecution', id: executionId });
  }

  selectExecution(executionId: string | null | undefined): void {
    if (!executionId || this.selectedExecutionId() === executionId) {
      return;
    }
    this.selectedExecutionId.set(executionId);
    this.loadOperationLogs();
  }

  loadOperationLogs(): void {
    const operationId = this.selectedExecutionId();
    if (!operationId) {
      this.resetLogs();
      return;
    }

    this.logRequestCancelled$.next();
    this.operationLogsLoading.set(true);
    this.monitoringService
      .monitoringOperationsKindOperationIdLogsGet(
        'JobExecution',
        operationId,
        undefined,
        undefined,
        this.operationLogLevel() || undefined,
        this.operationLogSearch() || undefined,
        200,
      )
      .pipe(
        takeUntil(this.logRequestCancelled$),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.operationLogsLoading.set(false)),
      )
      .subscribe({
        next: (response) => {
          this.operationLogs.set(response.entries ?? []);
          this.operationLogsAvailable.set(response.available ?? false);
          this.operationLogsProvider.set(response.provider ?? '');
          this.operationLogsMessage.set(response.statusMessage ?? '');
          this.operationLogsExternalUrl.set(this.safeExternalUrl(response.externalViewerUrl));
          this.operationLogsHasMore.set(response.hasMore ?? false);
        },
        error: () => {
          this.operationLogs.set([]);
          this.operationLogsAvailable.set(false);
          this.operationLogsMessage.set('Failed to load operation logs');
          this.operationLogsExternalUrl.set(null);
          this.operationLogsHasMore.set(false);
        },
      });
  }

  getStateSeverity(state?: string | null): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (state) {
      case 'Completed':
      case 'Information':
        return 'success';
      case 'Running':
      case 'Pending':
      case 'Debug':
      case 'Trace':
        return 'info';
      case 'Failed':
      case 'Error':
      case 'Critical':
        return 'danger';
      case 'Cancelled':
      case 'Cancelling':
      case 'Warning':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  trackLog(_index: number, entry: OperationLogRecordResponse): string {
    return `${entry.timestamp || ''}|${entry.message || ''}|${entry.traceId || ''}|${_index}`;
  }

  private loadExecutions(jobName: string): void {
    this.executionsRequestCancelled$.next();
    this.executionsLoading.set(true);
    this.executionsError.set(null);
    this.monitoringService
      .monitoringOperationsGet('JobExecution', undefined, undefined, undefined, jobName, undefined, undefined, undefined, undefined, undefined, 1, 20)
      .pipe(
        takeUntil(this.executionsRequestCancelled$),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.executionsLoading.set(false)),
      )
      .subscribe({
        next: (response) => {
          const items = (response.items ?? []).filter(
            (item) => (item.operationType || '').toLowerCase() === jobName.toLowerCase(),
          );
          this.executions.set(items);
          const firstId = items[0]?.id ?? null;
          if (firstId) {
            this.selectExecution(firstId);
          } else {
            this.resetLogs();
          }
        },
        error: () => {
          this.executions.set([]);
          this.executionsError.set('Failed to load monitored executions');
          this.resetLogs();
        },
      });
  }

  private resetLogs(): void {
    this.logRequestCancelled$.next();
    this.operationLogs.set([]);
    this.operationLogsLoading.set(false);
    this.operationLogsAvailable.set(false);
    this.operationLogsProvider.set('');
    this.operationLogsMessage.set('');
    this.operationLogsExternalUrl.set(null);
    this.operationLogsHasMore.set(false);
  }

  private safeExternalUrl(value?: string | null): string | null {
    if (!value) {
      return null;
    }
    try {
      const url = new URL(value);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
    } catch {
      return null;
    }
  }

  formatJson(value?: string | null): string {
    if (!value) {
      return '';
    }
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
}
